<template>
  <v-select
    ref="vSelectRef"
    v-bind="$attrs"
    :modelValue="modelValue"
    @update:modelValue="handleUpdate"
    :options="options"
    :label="label"
    :track-by="trackBy"
    :reduce="reduce"
    :clearable="clearable"
    :searchable="searchable"
    :filterable="filterable"
    :multiple="multiple"
    :disabled="disabled"
    :placeholder="placeholder"
    :loading="loading"
    append-to-body
    :class="['w-full', classProps]"
    @option:selected="(opt) => $emit('option:selected', opt)"
    @option:selecting="(opt) => $emit('option:selecting', opt)"
    @search="(query, loadingFn) => $emit('search', query, loadingFn)"
    @input="val => $emit('input', val)"
    @open="onOpen"
    @close="onClose"
    :calculate-position="withPopper"
    :select-on-tab="false"
  >
    <!-- Custom search slot to intercept Tab -->
    <template #search="{ attributes, events }">
      <input
        class="vs__search"
        v-bind="attributes"
        v-on="events"
        @keydown="onKeydown"
        @blur="onSearchBlur"
      />
    </template>

    <template v-if="$slots.option" #option="slotProps">
      <slot name="option" v-bind="slotProps" />
    </template>
  </v-select>
</template>

<script lang="ts" setup>
import { createPopper, Placement } from '@popperjs/core';
import { defineEmits, defineProps, nextTick, ref } from 'vue';

const props = defineProps({
  modelValue: {
    type: [Array, Object, String, Number, null],
    default: null,
  },
  options: {
    type: Array,
    required: true,
  },
  label: {
    type: String,
    default: 'label',
  },
  trackBy: {
    type: String,
    default: 'id',
  },
  reduce: {
    type: Function,
    default: undefined,
  },
  clearable: {
    type: Boolean,
    default: true,
  },
  searchable: {
    type: Boolean,
    default: true,
  },
  filterable: {
    type: Boolean,
    default: true,
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'Select',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  classProps: {
    type: String,
    default: "",
  },
});

const placement: Placement = 'bottom';

const emit = defineEmits([
  'update:modelValue',
  'option:selected',
  'option:selecting',
  'search',
  'input',
  'clear',
]);

const vSelectRef = ref<any>(null);
const isDropdownOpen = ref(false);

function handleUpdate(val: any) {
  emit('update:modelValue', val);
  if (val === null || (Array.isArray(val) && val.length === 0)) {
    emit('clear');
  }
}

function onOpen() {
  isDropdownOpen.value = true;
}

function onClose() {
  isDropdownOpen.value = false;
}

function handleSelecting(opt: any) {
  emit('option:selecting', opt);
}

// Get all focusable elements on the page
function getFocusable() {
  const selector = `
    a[href], button:not([disabled]), input:not([disabled]),
    select:not([disabled]), textarea:not([disabled]),
    [tabindex]:not([tabindex="-1"])
  `;
  return Array.from(document.querySelectorAll<HTMLElement>(selector))
    .filter(el => !el.hasAttribute('disabled') && el.offsetParent !== null);
}

// Tab key handler
function onKeydown(event: KeyboardEvent) {
  if (event.key === "Tab" && isDropdownOpen.value) {
    const vs = vSelectRef.value;
    const searchEl = event.target as HTMLElement;

    if (!vs || !vs.open) return;

    const pointer = vs.typeAheadPointer ?? 0;
    const option = vs.filteredOptions?.[pointer];
    if (!option) return;

    event.preventDefault(); // prevent default tab behavior

    // Select option and close dropdown
    vs.select(option);
    vs.open = false;
    isDropdownOpen.value = false;

    // Move focus to next focusable element
    nextTick(() => {
      const focusable = getFocusable();
      const currentIndex = focusable.indexOf(searchEl);
      const targetIndex = event.shiftKey ? currentIndex - 1 : currentIndex + 1;
      const targetEl = focusable[targetIndex];
      if (targetEl) targetEl.focus();
    });

    // Emit the selected value
    const valueToEmit = props.reduce ? props.reduce(option) : option;
    emit("update:modelValue", valueToEmit);
    emit("option:selected", option);
  }
}

// Close dropdown if search loses focus
function onSearchBlur() {
  setTimeout(() => {
    if (!vSelectRef.value?.open) {
      isDropdownOpen.value = false;
    }
  }, 150);
}

// Popper positioning
function withPopper(dropdownList: HTMLElement, component: any, { width }: { width: string }) {
  dropdownList.style.width = width;
  dropdownList.style.zIndex = '9999';

  const popper = createPopper(component.$refs.toggle, dropdownList, {
    placement: 'bottom' as Placement,
    strategy: 'absolute',
    modifiers: [
      { name: 'offset', options: { offset: [0, -1] } },
      { name: 'preventOverflow', options: { boundary: 'viewport', padding: 8 } },
      { name: 'flip', options: { fallbackPlacements: ['top', 'bottom'] } },
      {
        name: 'toggleClass',
        enabled: true,
        phase: 'write',
        fn({ state }) {
          component.$el.classList.toggle('drop-up', state.placement === 'top');
        },
      },
    ],
  });

  return () => popper.destroy();
}
</script>

<style>
@import 'vue-select/dist/vue-select.css';

.v-select.drop-up.vs--open .vs__dropdown-toggle {
  border-radius: 0 0 4px 4px;
  border-top-color: transparent;
  border-bottom-color: rgba(60, 60, 60, 0.26);
}

[data-popper-placement='top'] {
  border-radius: 4px 4px 0 0;
  border-top-style: solid;
  border-bottom-style: none;
  box-shadow: 0 -3px 6px rgba(0, 0, 0, 0.15);
}

.vs__dropdown-menu {
  pointer-events: auto !important;
  position: absolute !important;
  z-index: 10030 !important;
}

.vs__dropdown-option {
  cursor: pointer;
  user-select: none;
}
.assign-admin-button .vs__dropdown-toggle {
  height: 2.8rem;
  min-height: 2.8rem;
  background-color: var(--surface);
  border-radius: 6px;
}
</style>
